import React, {
    type FC,
    type ForwardedRef,
    forwardRef,
    type KeyboardEvent,
    type MouseEvent,
    type SVGProps,
} from 'react';

import { Typography } from '../Typography';

import { Badge, FabContainer, IconContainer, LabelContainer } from './styles';

export type FabProps = {
    badge?: number | 'plus';
    children?: string;
    classes?: string[];
    icon: FC<SVGProps<SVGSVGElement>>;
    onClick?: (e: MouseEvent<HTMLElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLElement>) => void;
    size?: 'small' | 'large' | number;
};

export const Fab = forwardRef(
    (
        { badge, children, classes, icon: IconElement, onClick, onKeyDown, size }: FabProps,
        ref: ForwardedRef<HTMLButtonElement>,
    ) => {
        const isNotificationCount = badge && typeof badge === 'number';

        // Set badge element, if present
        let badgeElement;
        if (badge) {
            let badgeLabel: string = badge as string;
            if (!isNotificationCount) {
                badgeLabel = '+';
            } else if (badge > 9) {
                badgeLabel = '9+';
            }

            badgeElement = (
                <Badge isNotificationCount={isNotificationCount || false}>
                    <Typography color="inherit" size={isNotificationCount ? 14 : 18} type="label">
                        {badgeLabel}
                    </Typography>
                </Badge>
            );
        }

        // Set text label, if present
        let label;
        if (children) {
            label = (
                <LabelContainer>
                    <Typography type="label" size={12}>
                        {children}
                    </Typography>
                </LabelContainer>
            );
        }

        let desktopSize;
        let mobileSize;
        if (typeof size === 'number') {
            desktopSize = size;
            mobileSize = size;
        } else {
            switch (size) {
                case 'small':
                    desktopSize = 40;
                    mobileSize = 32;
                    break;
                case 'large':
                    desktopSize = 60;
                    mobileSize = 40;
                    break;
            }
        }

        const additionalProps: { ref?: ForwardedRef<HTMLButtonElement> } = {};
        if (ref) {
            additionalProps.ref = ref;
        }

        return (
            <FabContainer
                className={classes ? classes.join(' ') : ''}
                desktopSize={desktopSize}
                mobileSize={mobileSize}
                onClick={onClick}
                onKeyDown={onKeyDown}
                {...additionalProps}
            >
                <IconContainer>
                    <IconElement />
                </IconContainer>
                {badgeElement}
                {label}
            </FabContainer>
        );
    },
);
Fab.displayName = 'Fab';
